import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import { apiPost } from '../../api/apiClient';
import logo from '../login/logo.png';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTPageLayout from '../common/RTPageLayout';
import { toastSuccess, toastError } from '../common/toast';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  PASSWORD_RULES,
  PASSWORD_RULES_MESSAGE,
  validatePasswordRules,
} from '../../utils/authHelpers';
import './LoginForm.css';
import '../common/rtCommon.css';
import tenantStorage from '../../utils/tenantStorage';

const PasswordRulesInfoIcon = () => (
  <span
    className="password-rules-info"
    tabIndex={0}
    aria-label={PASSWORD_RULES_MESSAGE}
  >
    <FaInfoCircle />
    <span className="password-rules-tooltip" role="tooltip">
      <strong>Password rules</strong>
      <ul>
        {PASSWORD_RULES.map((rule) => (
          <li key={rule}>{rule}</li>
        ))}
      </ul>
    </span>
  </span>
);

const ChangePasswordForm = ({ mode = 'CHANGE_PASSWORD', username = '' }) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentUsername, setCurrentUsername] = useState(username);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAppMode = mode === 'CHANGE_PASSWORD';
  const isFirstLogin = mode === 'NATIVE_MUST_CHANGE_PASSWORD';

  useEffect(() => {
    if (isFirstLogin) {
      setCurrentUsername(username);
    }
  }, [isFirstLogin, username]);

  useEffect(() => {
    if (mode === 'CHANGE_PASSWORD') {
      const accessToken = tenantStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/login', { state: { mode: 'SESSION_EXPIRED' } });
      }
    }
  }, [mode, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'CHANGE_PASSWORD' && !oldPassword.trim()) {
      toastError('Current password is required', 3000);
      return;
    }

    if (isFirstLogin && !currentUsername.trim()) {
      toastError('Username is required', 3000);
      return;
    }

    if (newPassword !== confirmPassword) {
      toastError('New password and confirm password do not match', 3000);
      return;
    }

    const validationErrors = validatePasswordRules(newPassword);
    if (validationErrors.length > 0) {
      toastError(validationErrors.join('. '), 4000);
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = { new_password: newPassword };
      if (isFirstLogin) {
        requestBody.username = currentUsername;
      }
      if (mode === 'CHANGE_PASSWORD') {
        requestBody.old_password = oldPassword;
      }

      const response = await apiPost(
        '/auth/native/change-password',
        requestBody,
      );
      if (response.data?.message) {
        toastSuccess('Password changed successfully!');
        tenantStorage.clear();
        navigate('/login', { state: { mode: 'PASSWORD_CHANGED' } });
      }
    } catch (err) {
      let errorMessage = 'Failed to change password. Please try again.';
      if (err.response && err.response.data && err.response.data.detail) {
        const detail = err.response.data.detail;
        const detailText =
          typeof detail === 'string'
            ? detail
            : detail.detail || detail.message || '';

        if (
          detailText.includes('Current password is incorrect') ||
          detailText.includes('old_password_incorrect')
        ) {
          errorMessage =
            'Current password is incorrect. Please check and try again.';
        } else if (
          detailText.includes('Password does not meet requirements') ||
          detailText.includes('weak_password')
        ) {
          errorMessage =
            'New password does not meet security requirements. Please choose a stronger password.';
        } else if (detailText) {
          errorMessage = detailText;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      toastError(errorMessage, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const formBody = (
    <div
      className={
        isAppMode
          ? 'change-password-panel change-password-form'
          : 'change-password-form'
      }
    >
      {!isAppMode && (
        <h3
          className="rt-title"
          style={{
            textAlign: 'center',
            marginBottom: '1.5rem',
            fontSize: '1.25rem',
          }}
        >
          Change Password
        </h3>
      )}
      <Form onSubmit={handleSubmit}>
        {mode === 'CHANGE_PASSWORD' && (
          <Form.Group controlId="formOldPassword" className="form-group">
            <Form.Label>Current Password:</Form.Label>
            <div className="password-input-container">
              <Form.Control
                type={showOldPassword ? 'text' : 'password'}
                placeholder="Enter current password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
              />
              <RTButton
                type="button"
                iconMode={true}
                className="password-toggle-btn"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </RTButton>
            </div>
          </Form.Group>
        )}
        <Form.Group controlId="formNewPassword" className="form-group">
          <Form.Label>New Password:</Form.Label>
          <div className="password-field-row">
            <div className="password-input-container">
              <Form.Control
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <RTButton
                type="button"
                iconMode={true}
                className="password-toggle-btn"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </RTButton>
            </div>
            <PasswordRulesInfoIcon />
          </div>
        </Form.Group>
        <Form.Group controlId="formConfirmPassword" className="form-group">
          <Form.Label>Confirm New Password:</Form.Label>
          <div className="password-field-row">
            <div className="password-input-container">
              <Form.Control
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <RTButton
                type="button"
                iconMode={true}
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </RTButton>
            </div>
            <PasswordRulesInfoIcon />
          </div>
        </Form.Group>
        <div className="form-buttons">
          <RTButton
            type="button"
            variant="secondary"
            disabled={isLoading}
            onClick={() => navigate(-1)}
          >
            Cancel
          </RTButton>
          <RTButton type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <RTSpinner size="small" />
                &nbsp;Changing...
              </>
            ) : (
              'Change Password'
            )}
          </RTButton>
        </div>
      </Form>
    </div>
  );

  if (isAppMode) {
    const backButton = (
      <RTButton type="button" variant="secondary" onClick={() => navigate(-1)}>
        &#8592; Back
      </RTButton>
    );
    return (
      <>
        <RTPageLayout
          title="Change Password"
          actions={backButton}
          mainSection
          activePath={location.pathname}
        >
          {formBody}
        </RTPageLayout>
        {isLoading && <RTSpinner fullScreen={true} />}
      </>
    );
  }

  return (
    <>
      <div className="login-container">
        <div>
          <img src={logo} alt="App Logo" className="login-logo" />
        </div>
        {formBody}
      </div>
      {isLoading && <RTSpinner fullScreen={true} />}
    </>
  );
};

export default ChangePasswordForm;
